import { useDragOperation } from '@dnd-kit/react'
import type { TestGroupingItem } from '@vedalib/editor/lib/elements'
import { motion } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import { useContext } from 'use-context-selector'

import { PreviewMode } from 'services/Store/Project/enums'

import TestGroupingContext from '../../TestGroupingContext'
import IconLeft from '../../assets/icons-for-elements-left.svg?react'
import IconRight from '../../assets/icons-for-elements-right.svg?react'
import { AnswerWrapperStyled } from '../../styled/TestGroupingStyled'
import useCarouselFocus from '../../useCarouselFocus'
import * as s from './AnswerStack.module.scss'
import PortalAnswerItem from './PortalAnswerItem'

interface IAnswerStackProps {
  answers: TestGroupingItem[]
}

const AnswerStack = ({ answers }: IAnswerStackProps) => {
  const { styles, activeIndex, value, mode, setSelectedAnswerId } = useContext(TestGroupingContext)
  const isEditor = mode.previewMode === PreviewMode.editor
  const items = value.items
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const totalItems = answers.length
  const prevLengthRef = useRef(totalItems)
  const stackRef = useRef<HTMLDivElement>(null)

  const { source: dragSource } = useDragOperation()

  useEffect(() => {
    if (isEditor && totalItems > prevLengthRef.current) {
      setCurrentIndex(0)
    }
    if (totalItems < prevLengthRef.current && currentIndex >= totalItems && totalItems > 0) {
      setCurrentIndex(totalItems - 1)
    }
    prevLengthRef.current = totalItems
  }, [totalItems, currentIndex, isEditor])

  useEffect(() => {
    const match = activeIndex?.match(/^items\.(\d+)\.items\.(\d+)\.label$/)
    if (!match) {
      return
    }
    const answer = items[+match[1]]?.items?.[+match[2]]
    if (!answer) {
      return
    }
    const idx = answers.findIndex((a) => a.value === answer.value)
    if (idx !== -1 && idx !== currentIndex) {
      setCurrentIndex(idx)
    }
  }, [activeIndex, items, answers, currentIndex])

  useCarouselFocus(stackRef, currentIndex, !isEditor)

  useEffect(() => {
    setIsDragging(false)
  }, [currentIndex])

  const nextIndex = (currentIndex + 1) % totalItems

  const handlePrevious = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    setSelectedAnswerId(undefined)
    setCurrentIndex((prev) => {
      const total = answers.length
      if (total <= 1) {
        return 0
      }
      return prev === 0 ? total - 1 : prev - 1
    })
  }

  const handleNext = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    setSelectedAnswerId(undefined)
    setCurrentIndex((prev) => {
      const total = answers.length
      if (total <= 1) {
        return 0
      }
      return (prev + 1) % total
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (dragSource) {
      return
    }
    if (e.key === 'ArrowLeft') {
      handlePrevious(e)
    } else if (e.key === 'ArrowRight') {
      handleNext(e)
    }
  }

  return (
    <div className={s.stackWrapper} onKeyDown={handleKeyDown} ref={stackRef} style={styles.common}>
      {totalItems > 1 && (
        <>
          <div
            className={s.navButtonLeft}
            onClick={handlePrevious}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ color: styles.common.color }}
          >
            <IconLeft fontSize={24} aria-hidden />
          </div>
          <div
            className={s.navButtonRight}
            onClick={handleNext}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ color: styles.common.color }}
          >
            <IconRight fontSize={24} aria-hidden />
          </div>
        </>
      )}
      <AnswerWrapperStyled $styles={styles}>
        <div className={s.stackContainer} style={styles.common}>
          {answers.map((item, index) => {
            const isCurrent = index === currentIndex
            const isNext = index === nextIndex && totalItems > 1
            const targetOpacity = isCurrent || (isNext && isDragging) ? 1 : 0

            return (
              <motion.div
                animate={{ opacity: targetOpacity }}
                aria-hidden={!isCurrent}
                className={s.cardWrapper}
                initial={{ opacity: 0.5 }}
                key={item.value}
                style={{
                  zIndex: isCurrent && isDragging ? 9999 : isCurrent ? 2 : 1,
                }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <PortalAnswerItem
                  answers={answers}
                  currentIndex={currentIndex}
                  index={index}
                  item={item}
                  onDraggingChange={isCurrent ? setIsDragging : undefined}
                  stackIndex={index}
                />
              </motion.div>
            )
          })}
        </div>
      </AnswerWrapperStyled>
    </div>
  )
}

export default AnswerStack
